export type GeometryUnit = "mm" | "in";
export type ThicknessUnit = "mm" | "in" | "mil";

export type ConfigurationStatus =
  | "Draft"
  | "Configured"
  | "Review Required"
  | "CAD Required"
  | "Warning"
  | "Error"
  | "Approved"
  | "Released";

export type WarningLevel = "info" | "warning" | "error" | "approved";

export type EquipmentKind = "blast-frame" | "induction-coil";

export interface ProjectInformation {
  projectNumber: string;
  projectName: string;
  customer: string;
  engineer: string;
  projectRevision: string;
  configurationDate: string;
  /** Spoolbase / yard location (edit modal). */
  spoolbaseLocation?: string;
}

export interface PipeSpecification {
  outsideDiameter: string;
  outsideDiameterUnit: GeometryUnit;
  wallThickness: string;
  wallThicknessUnit: ThicknessUnit;
  pipeGrade: string;
  pipeLength: string;
  pipeLengthUnit: GeometryUnit;
  pipeTolerance: string;
  pipeGeometry: string;
  cutbackLongSide: string;
  cutbackLongSideUnit: GeometryUnit;
  cutbackShortSide: string;
  cutbackShortSideUnit: GeometryUnit;
  cutbackPlusTolerance: string;
  cutbackMinusTolerance: string;
  cutbackShortPlusTolerance?: string;
  cutbackShortMinusTolerance?: string;
  chamferAngleDeg: string;
  chamferPlusToleranceDeg: string;
  chamferMinusToleranceDeg: string;
  cwcCutbackLongSide: string;
  cwcCutbackShortSide: string;
  cwcCutbackLongPlusTolerance?: string;
  cwcCutbackLongMinusTolerance?: string;
  cwcCutbackShortPlusTolerance?: string;
  cwcCutbackShortMinusTolerance?: string;
  ppToeLength: string;
  ppToeThickness: string;
  ppToeLengthPlusTolerance?: string;
  ppToeLengthMinusTolerance?: string;
  ppToeThicknessPlusTolerance?: string;
  ppToeThicknessMinusTolerance?: string;
  coatingPlusTolerance: string;
  coatingMinusTolerance: string;
  overlapOnFbe: string;
  /** Chamfer Multi-Pass PP — drives double-hinge autocoat. */
  cmppRequired?: boolean;
}

export interface ValidationError {
  field: string;
  message: string;
}

export interface ProcessSpecification {
  coatingType: string;
  coatingThickness: string;
  coatingThicknessUnit: ThicknessUnit;
  heatingRequirement: string;
  blastRequirement: string;
  surfacePreparation: string;
}

export interface ProjectSpecification {
  project: ProjectInformation;
  pipe: PipeSpecification;
  process: ProcessSpecification;
}

export interface EngineeringWarning {
  level: WarningLevel;
  title: string;
  message: string;
}

export interface CadCheck {
  id: string;
  label: string;
  confirmed: boolean;
}

export interface EquipmentFeature {
  name: string;
  required: boolean;
  selected: boolean;
}

export interface EquipmentOption {
  name: string;
  status: "Selected" | "Not Selected" | "Review Required";
}

export interface EngineeringDecision {
  question: string;
  input: string;
  rule: string;
  result: string;
  ruleVersion: string;
}

export interface CalculatedValue {
  label: string;
  value: string;
}

export interface EngineeringOverride {
  field: string;
  calculated: string;
  override: string;
  reason: string;
}

export interface EquipmentConfiguration {
  kind: EquipmentKind;
  configurationId: string;
  diameterCategory: string;
  lengthCategory?: string;
  status: ConfigurationStatus;
  ruleVersion: string;
  keyDimensions: CalculatedValue[];
  features: EquipmentFeature[];
  options: EquipmentOption[];
  calculations: CalculatedValue[];
  decisions: EngineeringDecision[];
  warnings: EngineeringWarning[];
  cadChecks: CadCheck[];
  overrides: EngineeringOverride[];
  notes: string;
}

export interface ConfigurationVersions {
  datasetRevision: string;
  blastFrameRuleVersion: string;
  inductionCoilRuleVersion: string;
  blastFrameLengthCategory?: string;
  inductionCoilLengthCategory?: string;
}

export interface InputSummaryValue {
  label: string;
  value: string;
}

export interface InputSummarySection {
  section: string;
  values: InputSummaryValue[];
}

export interface ConfigurationResult {
  configurationId: string;
  revision: string;
  status: ConfigurationStatus;
  createdAt: string;
  versions?: ConfigurationVersions;
  inputSummary?: InputSummarySection[];
  project: ProjectInformation;
  pipe: PipeSpecification;
  process: ProcessSpecification;
  blastFrame: EquipmentConfiguration | null;
  inductionCoil: EquipmentConfiguration | null;
  warnings: EngineeringWarning[];
  errorMessage?: string;
}

export interface BuildSpecification {
  configurationId: string;
  revision: string;
  status: ConfigurationStatus;
  project: ProjectInformation;
  equipment: EquipmentKind;
  equipmentLabel: string;
  equipmentConfigurationId: string;
  classification: CalculatedValue[];
  requiredFeatures: string[];
  selectedOptions: string[];
  engineeringParameters: CalculatedValue[];
  cadRequirements: string;
  engineeringStatus: string;
}

export interface ConfigurationService {
  listConfigurations(): Promise<ConfigurationResult[]>;
  createConfiguration(project: ProjectSpecification): Promise<ConfigurationResult>;
  updateConfiguration(
    configurationId: string,
    project: ProjectSpecification,
  ): Promise<ConfigurationResult>;
  deleteConfiguration(configurationId: string): Promise<void>;
  getConfiguration(configurationId: string): Promise<ConfigurationResult>;
  getBuildSpecification(
    configurationId: string,
    equipment: EquipmentKind,
  ): Promise<BuildSpecification>;
  updateCadChecks(
    configurationId: string,
    equipment: EquipmentKind,
    checks: CadCheck[],
  ): Promise<ConfigurationResult>;
  applyOverride(
    configurationId: string,
    equipment: EquipmentKind,
    override: EngineeringOverride,
  ): Promise<ConfigurationResult>;
  getCatalogue(): Promise<OnPipeCatalogue>;
  createBlastFrameFrameSize(
    item: BlastFrameCatalogueFrameSize,
  ): Promise<BlastFrameCatalogueFrameSize>;
  createBlastFrameVersion(item: BlastFrameCatalogueVersion): Promise<BlastFrameCatalogueVersion>;
  createBlastFrameKite(item: BlastFrameCatalogueKite): Promise<BlastFrameCatalogueKite>;
  createBlastFrameCarriageRow(
    item: BlastFrameCatalogueCarriageRow,
  ): Promise<BlastFrameCatalogueCarriageRow>;
  deleteBlastFrameFrameSize(label: string): Promise<void>;
  deleteBlastFrameVersion(versionId: string): Promise<void>;
  deleteBlastFrameKite(kiteId: string): Promise<void>;
  deleteBlastFrameCarriageRow(frameSize: string): Promise<void>;
  createInductionCoilFrame(
    item: InductionCoilCatalogueFrame,
  ): Promise<InductionCoilCatalogueFrame>;
  createInductionCoilVersion(
    item: InductionCoilCatalogueVersion,
  ): Promise<InductionCoilCatalogueVersion>;
  deleteInductionCoilFrame(frameId: string): Promise<void>;
  deleteInductionCoilVersion(versionId: string): Promise<void>;
  getAssumptions(): Promise<OnPipeAssumptions>;
  updateAssumptions(assumptions: OnPipeAssumptions): Promise<OnPipeAssumptions>;
}

export interface BlastFrameAssumptions {
  smallHeadMaxSteelOdMm: number;
  smallHeadSealChordMm: number;
  largeHeadSealChordMm: number;
  twoTieBarHeadPlateWidthMm: number;
  fourTieBarHeadPlateWidthMm: number;
  chamferSideClearanceMm: number;
  cwcOverallLengthMarginMm: number;
}

export interface InductionCoilAssumptions {
  coatingClearanceMm: number;
  preferredNonCwcVersionId: string;
  stepDownCoatingThicknessMm: number;
  chamferClearanceMm: number;
  cwcLengthMarginMm: number;
}

export interface OnPipeAssumptions {
  blastFrame: BlastFrameAssumptions;
  inductionCoil: InductionCoilAssumptions;
}

export interface BlastFrameCatalogueFrameSize {
  label: string;
  index: number;
  minMm: number;
  maxMm: number;
}

export interface BlastFrameCatalogueVersion {
  id: string;
  index: number;
  innerWheelLengthMm: number;
  overallLengthMm: number;
  carriageLengthMm: number;
}

export interface BlastFrameCatalogueKite {
  id: string;
  heads: string;
  requiresTwoTieBarBlast: boolean;
  requiresFourTieBarBlast: boolean;
  steelOdInnerOffsetMm: number;
  steelOdOuterOffsetMm: number;
  clearanceOffsetsMm: number[];
  clearanceMarginMm: number;
  usesSmallSeal: boolean;
}

export interface BlastFrameCatalogueCarriageRow {
  frameSize: string;
  outerBlastCarriageHolePcdMm: number;
}

export interface BlastFrameCatalogue {
  ruleVersion: string;
  datasetRevision?: string;
  smallHeadMaxSteelOdMm?: number;
  smallHeadSealChordMm?: number;
  largeHeadSealChordMm?: number;
  twoTieBarHeadPlateWidthMm?: number;
  fourTieBarHeadPlateWidthMm?: number;
  chamferSideClearanceMm?: number;
  cwcOverallLengthMarginMm?: number;
  frameSizes: BlastFrameCatalogueFrameSize[];
  versions: BlastFrameCatalogueVersion[];
  kites: BlastFrameCatalogueKite[];
  carriageRows?: BlastFrameCatalogueCarriageRow[];
  carriageCoatingInnerOffsetMm?: number;
  carriageCoatingOuterOffsetMm?: number;
}

export interface InductionCoilCatalogueFrame {
  nbIn: number;
  index: number;
  clearanceType: string;
  idMm: number;
}

export interface InductionCoilCatalogueVersion {
  id: string;
  index: number;
  bottomEndLengthMm: number;
  insideWheelsLengthMm: number;
  totalLengthSwivelPlateMm: number;
  heatLengthMm: number;
  totalLengthTufnolDiscsMm: number;
  insideTufnolDiscsLengthMm: number;
}

export interface InductionCoilCatalogue {
  ruleVersion: string;
  datasetRevision?: string;
  coatingClearanceMm?: number;
  preferredNonCwcVersionId?: string;
  stepDownCoatingThicknessMm?: number;
  frames: InductionCoilCatalogueFrame[];
  versions: InductionCoilCatalogueVersion[];
}

export interface OnPipeCatalogue {
  datasetRevision: string;
  blastFrame: BlastFrameCatalogue;
  inductionCoil: InductionCoilCatalogue;
}

export const emptyProjectInformation = (): ProjectInformation => ({
  projectNumber: "",
  projectName: "",
  customer: "",
  engineer: "",
  projectRevision: "A",
  configurationDate: new Date().toISOString().slice(0, 10),
  spoolbaseLocation: "",
});

export const emptyPipeSpecification = (): PipeSpecification => ({
  outsideDiameter: "",
  outsideDiameterUnit: "mm",
  wallThickness: "",
  wallThicknessUnit: "mm",
  pipeGrade: "",
  pipeLength: "",
  pipeLengthUnit: "mm",
  pipeTolerance: "",
  pipeGeometry: "Straight",
  cutbackLongSide: "",
  cutbackLongSideUnit: "mm",
  cutbackShortSide: "",
  cutbackShortSideUnit: "mm",
  cutbackPlusTolerance: "",
  cutbackMinusTolerance: "",
  cutbackShortPlusTolerance: "",
  cutbackShortMinusTolerance: "",
  chamferAngleDeg: "",
  chamferPlusToleranceDeg: "",
  chamferMinusToleranceDeg: "",
  cwcCutbackLongSide: "",
  cwcCutbackShortSide: "",
  cwcCutbackLongPlusTolerance: "",
  cwcCutbackLongMinusTolerance: "",
  cwcCutbackShortPlusTolerance: "",
  cwcCutbackShortMinusTolerance: "",
  ppToeLength: "",
  ppToeThickness: "",
  ppToeLengthPlusTolerance: "",
  ppToeLengthMinusTolerance: "",
  ppToeThicknessPlusTolerance: "",
  ppToeThicknessMinusTolerance: "",
  coatingPlusTolerance: "",
  coatingMinusTolerance: "",
  overlapOnFbe: "",
  cmppRequired: false,
});

export const emptyProcessSpecification = (): ProcessSpecification => ({
  coatingType: "",
  coatingThickness: "",
  coatingThicknessUnit: "mm",
  heatingRequirement: "",
  blastRequirement: "",
  surfacePreparation: "",
});
